import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { PermissionRole } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon } from 'src/common/components/icon';
import { useOpenModal } from 'src/common/components/next-modal';
import { getIllegalText } from 'src/common/const';
import { request } from 'src/common/request';
import { useReadonly } from 'src/hooks/page';
import { useGetIsOwner } from 'src/hooks/share/use-owner';
import { useRestorePermission } from 'src/hooks/share/use-permission-utils';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { useSpaceDomainPath } from 'src/hooks/space';
import { TRANSACTION_FIRE } from 'src/redux/actions';
import { dispatch } from 'src/redux/store';
import { bizTracker } from 'src/utils/biz-tracker';
import { getBlockPageLikeTitle } from 'src/utils/get-untitled-name';
import { usePickBlock } from 'src/utils/pick-block';
import { AppealResult } from '../appeal-result';
import { ShareAudit } from '../share-audit';
import type { ShareProps } from '../types';

export const ShareMainFooter: FC<Pick<ShareProps, 'uuid'>> = ({ uuid }) => {
  const { t } = useAppTranslation();
  const { parentId, isRestricted, illegal, role, shared } = usePermissions(uuid, {
    wait: 100,
  });
  // 继承的被风控了
  const { illegal: parentIsIllegal } = usePermissions(parentId ?? uuid, { wait: 100 });
  const readonly = useReadonly(uuid);
  const openAudit = useOpenAudit(uuid);
  const restorePermission = useRestorePermission(uuid);
  const openModal = useOpenModal();
  const block = usePickBlock(uuid, ['data'], ['segments']);
  const showAudit = useShowAudit(uuid);
  const domainPath = useSpaceDomainPath(block?.spaceId);

  if (!isRestricted && !illegal) return null;
  if (!block) return null;
  // 根目录受限时不展示
  if (block.parentId === block.spaceId) return null;
  // 有不少用户反馈有子页面不显示，其实是不知道为啥断开了继承关系，这里直接显示
  // if (!shared) return null;

  const onClick = () => {
    openModal.warning({
      title: (
        <div className="px-5 w-80">
          <div>{t('components.share.share_main_footer.confirm_restore_inheritance')}</div>
          <div>{t('components.share.share_main_footer.inherit_and_keep_collaborators')}</div>
        </div>
      ),
      confirmText: t('common.confirm'),
      confirm: () => {
        restorePermission();
        dispatch(TRANSACTION_FIRE());
        bizTracker.event('restore_to_inherit');
      },
    });
  };

  return (
    <div className="flex justify-between items-center py-4 px-3 text-grey3 bg-grey9 border-t border-grey6 text-t2">
      <div className="flex items-center text-t4">
        {(illegal || parentId) && <Icon name="IcShareDanger" size="small" className="mr-1" />}

        {illegal ? (
          <span>
            {t('components.share.share_main_footer.page_violates')}
            <NavLink
              to={`${domainPath}/${uuid}`}
              className="ml-1 text-active_color underline animate-click"
            >
              {t('components.share.share_main_footer.platform_sharing_policy')}
            </NavLink>
            {t('components.share.share_main_footer.cannot_share')}
          </span>
        ) : (
          <>
            {parentId && (
              <div className="flex font-medium text-grey3 text-t4">
                {isRestricted
                  ? t('components.share.share_main_footer.not_inherit_from_parent')
                  : t('components.share.share_main_footer.inherit_from_parent')}
                <NavLink
                  to={`${domainPath}/${parentId}`}
                  className={'text-black animate-click max-w-[80px] text-ellipsis mx-1'}
                >
                  {parentIsIllegal
                    ? getIllegalText()
                    : getBlockPageLikeTitle(block.type, block.data.segments)}
                </NavLink>
                {isRestricted
                  ? t('components.share.share_main_footer.permissions_of')
                  : t('components.share.share_main_footer.sharing_of')}
              </div>
            )}
          </>
        )}
      </div>

      {!readonly &&
        (illegal && showAudit ? (
          <div
            onClick={openAudit}
            className="shrink-0 font-medium text-black text-t4-medium animate-click"
          >
            {t('common.appeal')}
          </div>
        ) : (
          isRestricted &&
          role === PermissionRole.EDITOR && (
            <div
              onClick={onClick}
              className="shrink-0 self-start mr-1 text-black text-t4-medium animate-click"
            >
              {t('components.share.share_main_footer.restore_permission_inheritance')}
            </div>
          )
        ))}
    </div>
  );
};

export const useOpenAudit = (
  uuid: string,
  opt?: {
    blockType?: 'block' | 'form';
  }
) => {
  const { t } = useAppTranslation();
  const openModal = useOpenModal();

  return useCallback(() => {
    // https://dev.allflow.cn/swagger/#/editor/editor.shareCheck
    enum ShareCheckType {
      report = 'complain', // 举报
      justice = 'appeal', // 申诉
    }

    void request.editor.shareCheck(uuid, ShareCheckType.justice).then((canReport) => {
      if (canReport === false) {
        openModal.modal({
          content: ({ onCloseModal: onClose }) => (
            <AppealResult success={false} type="justice" onCloseModal={onClose} />
          ),
        });
      } else {
        openModal.modal({
          content: ({ onCloseModal }) => (
            <ShareAudit
              blockType={opt?.blockType}
              type="justice"
              uuid={uuid}
              onCloseModal={onCloseModal}
            />
          ),
        });
      }
    });
  }, [uuid, openModal, opt?.blockType, t]);
};

export const useShowAudit = (uuid: string) => {
  const { role } = usePermissions(uuid);
  const getIsOwner = useGetIsOwner();

  const showBtn =
    [
      PermissionRole.COMMENTER,
      PermissionRole.READER,
      PermissionRole.EDITOR,
      PermissionRole.WRITER,
    ].includes(role) || getIsOwner(uuid);
  return showBtn;
};
